import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { endpoints } from "../lib/api";
import { Overline, Section, PrimaryButton } from "../components/UI";
import { SEO } from "../components/SEO";
import { ArrowLeft } from "lucide-react";
import { resolveApprovedCondition } from "../data/approvedConditions";

export function JourneysHub() {
  const [items, setItems] = useState([]);
  useEffect(() => { endpoints.journeys().then(setItems).catch(() => {}); }, []);
  return (
    <Section>
      <SEO/>
      <Overline>Patient Journeys</Overline>
      <h1 className="font-serif text-4xl md:text-6xl text-navy mt-3 tracking-tight leading-none">Where are you in your journey?</h1>
      <p className="text-lg text-stone2 mt-5 max-w-2xl">Curated pathways — from planning a pregnancy to postpartum and dedicated high-risk tracks.</p>
      <div className="mt-14 relative border-l-2 border-line pl-8 space-y-10">
        {items.map((j, i) => (
          <div key={j.slug} className="relative">
            <div className="absolute -left-[42px] top-2 w-4 h-4 rounded-full bg-terracotta border-4 border-bone"/>
            <Link to={`/journeys/${j.slug}`} data-testid={`jh-${j.slug}`} className="group block">
              <div className="mono text-[10px] uppercase tracking-widest text-prestige">{j.week_range}</div>
              <div className="font-serif text-3xl text-navy mt-1 group-hover:underline underline-offset-4">{j.name}</div>
              <p className="text-stone2 mt-2 max-w-3xl">{j.description}</p>
            </Link>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function JourneyDetail() {
  const { slug } = useParams();
  const [j, setJ] = useState(null);
  useEffect(() => { endpoints.journey(slug).then(setJ).catch(() => setJ({ error: true })); }, [slug]);
  if (!j) return <Section><p>Loading…</p></Section>;
  if (j.error) return <Section><p>Journey not found.</p></Section>;
  return (
    <Section>
      <SEO
        title={`${j.name} — Patient Journey · Dr. Tania Gurdip Singh`}
        description={(j.description || "").slice(0, 300)}
        type="article"
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Journeys", item: "/journeys" },
          { name: j.name, item: `/journeys/${j.slug}` },
        ]}
      />
      <Link to="/journeys" className="mono text-xs uppercase tracking-widest text-stone2 hover:text-navy inline-flex items-center gap-1.5"><ArrowLeft size={14} strokeWidth={1.5}/> All journeys</Link>
      <div className="mt-6"><Overline>{j.week_range}</Overline></div>
      <h1 className="font-serif text-4xl md:text-6xl text-navy mt-3 tracking-tight leading-none">{j.name}</h1>
      <p className="text-lg text-stone2 mt-5 max-w-3xl">{j.description}</p>

      {j.steps?.length > 0 && (
        <div className="mt-14 space-y-8">
          {j.steps.map((s, i) => (
            <div key={i} className="border-l-2 border-prestige pl-6 py-3">
              <div className="mono text-[10px] uppercase tracking-widest text-prestige">Step {i+1}</div>
              <div className="font-serif text-2xl text-navy mt-1">{s.title}</div>
              {s.articles?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {s.articles.map(as => <Link key={as} to={`/articles/${as}`} className="text-sm text-navy underline underline-offset-4 block">{as.replace(/-/g,' ')}</Link>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {j.linked_condition_slugs?.length > 0 && (() => {
        // Map legacy DB slugs to approved routes/titles; drop any slug that
        // does not resolve to an approved Condition so no unapproved slug
        // can leak into a chip.
        const approvedChips = j.linked_condition_slugs
          .map((s) => {
            const r = resolveApprovedCondition(s);
            return r ? { slug: s, route: r.route, title: r.title } : null;
          })
          .filter(Boolean)
          // De-duplicate by route in case two legacy slugs resolve to the same approved one.
          .filter((v, i, arr) => arr.findIndex((x) => x.route === v.route) === i);
        if (approvedChips.length === 0) return null;
        return (
          <div className="mt-14">
            <Overline>Relevant Conditions</Overline>
            <div className="mt-4 flex flex-wrap gap-3" data-testid="journey-condition-chips">
              {approvedChips.map((c) => (
                <Link
                  key={c.route}
                  to={c.route}
                  data-testid={`journey-condition-chip-${c.slug}`}
                  className="border border-line rounded-full px-4 py-2 text-sm text-navy hover:bg-navy hover:text-white transition-colors"
                >
                  {c.title}
                </Link>
              ))}
            </div>
          </div>
        );
      })()}
    </Section>
  );
}
